import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
/* Its own module so the auth store can clear it on a session change — see the
   note there for the stale-data bug that caused. */
import { queryClient, clearQueryCache } from '@/lib/queryClient';
import { Loader2 } from 'lucide-react';
import { setUnauthenticatedHandler } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { ToastLayer } from '@/components/ToastLayer';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AppShell } from '@/components/layout/AppShell';
import { LoginPage } from '@/pages/LoginPage';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { DashboardPage } from '@/pages/DashboardPage';

/**
 * Everything past login is lazily loaded.
 *
 * Login, the shell and the dashboard ship in the entry chunk because they are
 * needed immediately; every other screen arrives when it is first opened. An
 * employee never downloads the admin console, and nobody downloads the course
 * builder until they open it.
 */
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const MyDepartmentsPage = lazy(() => import('@/pages/MyDepartmentsPage').then((m) => ({ default: m.MyDepartmentsPage })));
const LearningSparkPage = lazy(() => import('@/pages/admin/LearningSparkPage').then((m) => ({ default: m.LearningSparkPage })));
const MyCoursesPage = lazy(() => import('@/pages/learn/MyCoursesPage').then((m) => ({ default: m.MyCoursesPage })));
const CataloguePage = lazy(() => import('@/pages/learn/CataloguePage').then((m) => ({ default: m.CataloguePage })));
const WishlistPage = lazy(() => import('@/pages/learn/WishlistPage').then((m) => ({ default: m.WishlistPage })));
const CoursePlayerPage = lazy(() => import('@/pages/learn/CoursePlayerPage').then((m) => ({ default: m.CoursePlayerPage })));
const MyAssessmentsPage = lazy(() => import('@/pages/learn/MyAssessmentsPage').then((m) => ({ default: m.MyAssessmentsPage })));
const AttemptPlayerPage = lazy(() => import('@/pages/learn/AttemptPlayerPage').then((m) => ({ default: m.AttemptPlayerPage })));
const AttemptResultPage = lazy(() => import('@/pages/learn/AttemptResultPage').then((m) => ({ default: m.AttemptResultPage })));
const MyCertificatesPage = lazy(() => import('@/pages/learn/MyCertificatesPage').then((m) => ({ default: m.MyCertificatesPage })));
const ProfilePage = lazy(() => import('@/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const AchievementsPage = lazy(() => import('@/pages/learn/AchievementsPage').then((m) => ({ default: m.AchievementsPage })));
const VerifyCertificatePage = lazy(() => import('@/pages/VerifyCertificatePage').then((m) => ({ default: m.VerifyCertificatePage })));

const ActionCenterPage = lazy(() => import('@/pages/admin/ActionCenterPage').then((m) => ({ default: m.ActionCenterPage })));
const UsersPage = lazy(() => import('@/pages/admin/UsersPage').then((m) => ({ default: m.UsersPage })));
const RolesPage = lazy(() => import('@/pages/admin/RolesPage').then((m) => ({ default: m.RolesPage })));
const DepartmentsPage = lazy(() => import('@/pages/admin/OrgUnitsPage').then((m) => ({ default: m.DepartmentsPage })));
const DesignationsPage = lazy(() => import('@/pages/admin/OrgUnitsPage').then((m) => ({ default: m.DesignationsPage })));
const BulkImportPage = lazy(() => import('@/pages/admin/BulkImportPage').then((m) => ({ default: m.BulkImportPage })));
const CoursesPage = lazy(() => import('@/pages/admin/CoursesPage').then((m) => ({ default: m.CoursesPage })));
const CourseBuilderPage = lazy(() => import('@/pages/admin/CourseBuilderPage').then((m) => ({ default: m.CourseBuilderPage })));
const CategoriesPage = lazy(() => import('@/pages/admin/CategoriesPage').then((m) => ({ default: m.CategoriesPage })));
const AssessmentsPage = lazy(() => import('@/pages/admin/AssessmentsPage').then((m) => ({ default: m.AssessmentsPage })));
const AssessmentBuilderPage = lazy(() => import('@/pages/admin/AssessmentBuilderPage').then((m) => ({ default: m.AssessmentBuilderPage })));
const EvaluationQueuePage = lazy(() => import('@/pages/admin/EvaluationQueuePage').then((m) => ({ default: m.EvaluationQueuePage })));
const RebuttalsPage = lazy(() => import('@/pages/admin/RebuttalsPage').then((m) => ({ default: m.RebuttalsPage })));
const EvaluateAttemptPage = lazy(() => import('@/pages/admin/EvaluateAttemptPage').then((m) => ({ default: m.EvaluateAttemptPage })));
const CertificatesPage = lazy(() => import('@/pages/admin/CertificatesPage').then((m) => ({ default: m.CertificatesPage })));
const ReportsPage = lazy(() => import('@/pages/admin/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const LeadersActivityPage = lazy(() => import('@/pages/admin/LeadersActivityPage').then((m) => ({ default: m.LeadersActivityPage })));
const DepartmentReportPage = lazy(() => import('@/pages/admin/DepartmentReportPage').then((m) => ({ default: m.DepartmentReportPage })));
const AuditLogPage = lazy(() => import('@/pages/admin/AuditLogPage').then((m) => ({ default: m.AuditLogPage })));
const SettingsPage = lazy(() => import('@/pages/admin/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const AnnouncementsPage = lazy(() => import('@/pages/AnnouncementsPage').then((m) => ({ default: m.AnnouncementsPage })));
const CalendarPage = lazy(() => import('@/pages/CalendarPage').then((m) => ({ default: m.CalendarPage })));

function RouteFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
    </div>
  );
}

export function App() {
  const bootstrap = useAuthStore((s) => s.bootstrap);
  const setUser = useAuthStore((s) => s.setUser);

  useEffect(() => {
    /**
     * When a refresh finally fails, drop the user so guards send them to
     * login — and drop what they fetched with them.
     *
     * A session can end without anybody pressing sign out. Leaving the cache
     * behind on this path is the same bug by a quieter route: the next person
     * to sign in on this browser inherits the last one's screens.
     */
    setUnauthenticatedHandler(() => {
      clearQueryCache();
      setUser(null);
    });
    void bootstrap();
  }, [bootstrap, setUser]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          {/* Public — someone locked out cannot authenticate to get back in. */}
          <Route
            path="/forgot-password"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ForgotPasswordPage />
              </Suspense>
            }
          />
          <Route
            path="/reset-password"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ResetPasswordPage />
              </Suspense>
            }
          />

          {/* Public — no authentication. Anyone holding a certificate ID can
              confirm it, which is the point of a verification page. */}
          <Route
            path="/verify/:publicId?"
            element={
              <Suspense fallback={<RouteFallback />}>
                <VerifyCertificatePage />
              </Suspense>
            }
          />

          {/* Outside the shell: nothing else is reachable until the password is set. */}
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePasswordPage />
              </ProtectedRoute>
            }
          />

          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<DashboardPage />} />

            {/* The departments a manager governs. `scope.department` rather
                than a management permission: this is a read-only view of their
                own team, and it is the one thing that stays on their menu now
                that Manage is off it. */}
            <Route
              path="/my-departments"
              element={<Lazy requires={['scope.department']}><MyDepartmentsPage /></Lazy>}
            />

            {/* ── Learner ─────────────────────────────────────────── */}
            <Route path="/my-courses" element={<Lazy requires={['learning.view_own']}><MyCoursesPage /></Lazy>} />
            <Route path="/catalogue" element={<Lazy requires={['learning.browse_catalogue']}><CataloguePage /></Lazy>} />
            <Route path="/wishlist" element={<Lazy requires={['learning.wishlist']}><WishlistPage /></Lazy>} />
            {/* No permission gate: the API decides whether this specific course
                is assigned or catalogue-browsable for this user. */}
            <Route path="/courses/:courseId" element={<Lazy><CoursePlayerPage /></Lazy>} />
            <Route path="/my-assessments" element={<Lazy requires={['assessment.take']}><MyAssessmentsPage /></Lazy>} />
            <Route path="/my-assessments/attempt/:attemptId" element={<Lazy requires={['assessment.take']}><AttemptPlayerPage /></Lazy>} />
            <Route path="/my-assessments/results/:attemptId" element={<Lazy><AttemptResultPage /></Lazy>} />
            <Route path="/my-certificates" element={<Lazy><MyCertificatesPage /></Lazy>} />
            <Route path="/achievements" element={<Lazy><AchievementsPage /></Lazy>} />
            <Route path="/announcements" element={<Lazy requires={['announcement.view']}><AnnouncementsPage /></Lazy>} />
            <Route path="/calendar" element={<Lazy requires={['calendar.view']}><CalendarPage /></Lazy>} />
            {/* No permission gate — the API only ever returns the caller's own
                record, so there is nothing here another user could reach. */}
            <Route path="/profile" element={<Lazy><ProfilePage /></Lazy>} />

            {/* ── Admin ───────────────────────────────────────────── */}
            {/* Each route repeats its permission, so a direct URL is refused
                even though the sidebar hid the link. */}
            <Route
              path="/admin/learning-spark"
              element={<Lazy requires={['challenge.view']}><LearningSparkPage /></Lazy>}
            />
            {/*
              Organisation scope, not a feature permission. The page is one
              view across the whole tier — every list on it is somebody else's
              work — and the API refuses it to anyone else regardless.
            */}
            <Route path="/admin/action-center" element={<Lazy requires={['scope.organization']}><ActionCenterPage /></Lazy>} />
            <Route path="/admin/users" element={<Lazy requires={['user.view']}><UsersPage /></Lazy>} />
            <Route path="/admin/roles" element={<Lazy requires={['role.view']}><RolesPage /></Lazy>} />
            <Route path="/admin/departments" element={<Lazy requires={['department.view']}><DepartmentsPage /></Lazy>} />
            <Route path="/admin/designations" element={<Lazy requires={['designation.view']}><DesignationsPage /></Lazy>} />
            {/* Six operations, six permissions — any one of them opens it. The
                optional :type preselects one, so a link from the calendar lands
                on the calendar upload rather than on whichever tab is first. */}
            <Route
              path="/admin/bulk/:type?"
              element={
                <Lazy
                  requiresAny={[
                    'user.import',
                    'course.bulk_assign',
                    'assessment.bulk_assign',
                    'question.import',
                    'certificate.bulk_issue',
                    'calendar.create',
                  ]}
                >
                  <BulkImportPage />
                </Lazy>
              }
            />
            <Route path="/admin/courses" element={<Lazy requires={['course.view']}><CoursesPage /></Lazy>} />
            <Route path="/admin/courses/:courseId" element={<Lazy requires={['course.view']}><CourseBuilderPage /></Lazy>} />
            <Route path="/admin/categories" element={<Lazy requires={['category.view']}><CategoriesPage /></Lazy>} />
            <Route path="/admin/assessments" element={<Lazy requires={['assessment.view']}><AssessmentsPage /></Lazy>} />
            <Route path="/admin/assessments/:assessmentId" element={<Lazy requires={['assessment.edit']}><AssessmentBuilderPage /></Lazy>} />
            <Route path="/admin/evaluation" element={<Lazy requires={['evaluation.view_queue']}><EvaluationQueuePage /></Lazy>} />
            <Route path="/admin/question-reports" element={<Lazy requires={['rebuttal.view']}><RebuttalsPage /></Lazy>} />
            <Route path="/admin/evaluation/:attemptId" element={<Lazy requires={['evaluation.view_queue']}><EvaluateAttemptPage /></Lazy>} />
            <Route path="/admin/certificates" element={<Lazy requires={['certificate.view']}><CertificatesPage /></Lazy>} />
            <Route path="/admin/reports" element={<Lazy requires={['report.view']}><ReportsPage /></Lazy>} />
            {/* The drill-down from a department row. Same permission — it is
                the same data, narrowed, and the API scopes it besides. */}
            <Route
              path="/admin/reports/department/:departmentId"
              element={<Lazy requires={['report.view']}><DepartmentReportPage /></Lazy>}
            />
            {/* Trainers and managers, measured. Same permission as the other
                reports — it is a report, and the API scopes it besides. */}
            <Route
              path="/admin/leaders-activity"
              element={<Lazy requires={['report.view']}><LeadersActivityPage /></Lazy>}
            />
            <Route path="/admin/audit" element={<Lazy requires={['audit.view']}><AuditLogPage /></Lazy>} />
            <Route path="/admin/settings" element={<Lazy requires={['settings.view']}><SettingsPage /></Lazy>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      {/* Its own component, because the layer is more than a Toaster now —
          see `ToastLayer` for why it takes no clicks. */}
      <ToastLayer />
    </QueryClientProvider>
  );
}

/** Pairs the permission guard with the Suspense boundary each lazy route needs. */
function Lazy({
  children,
  requires,
  requiresAny,
}: {
  children: React.ReactNode;
  requires?: React.ComponentProps<typeof ProtectedRoute>['requires'];
  requiresAny?: React.ComponentProps<typeof ProtectedRoute>['requiresAny'];
}) {
  return (
    <ProtectedRoute requires={requires} requiresAny={requiresAny}>
      <Suspense fallback={<RouteFallback />}>{children}</Suspense>
    </ProtectedRoute>
  );
}
